// /app/frontend/src/pages/compliance/vendor/VendorRegistryPage.jsx
// Vendor Registry Dashboard - Spec v1.0 Implementation

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { vendorRegistryAPI, dueDiligenceAPI, clientsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useGlobalContext } from '../../../contexts/GlobalContext';
import {
  Plus, Search, AlertTriangle, Loader2, ChevronUp, ChevronDown,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

// Risk pill colors per spec
const RISK_PILL = {
  HIGH:         { bg: '#FCEBEB', text: '#A32D2D', dot: '#E24B4A', label: 'High' },
  MEDIUM:       { bg: '#FAEEDA', text: '#854F0B', dot: '#BA7517', label: 'Medium' },
  LOW:          { bg: '#EAF3DE', text: '#3B6D11', dot: '#639922', label: 'Low' },
  NOT_ASSESSED: { bg: '#F0F0F0', text: '#888888', dot: '#BBBBBB', label: 'Not assessed' },
};

// Helper functions - field mapping
const mapRiskTier = (t) => {
  const m = { high: 'HIGH', medium: 'MEDIUM', low: 'LOW', not_assessed: 'NOT_ASSESSED' };
  return m[t] || 'NOT_ASSESSED';
};

const mapDpa = (dpa_signed_or_has_dpa) => {
  // Handles both SimplifiedVendor.dpa_signed and VendorV2.has_dpa
  if (dpa_signed_or_has_dpa === true) return 'signed';
  if (dpa_signed_or_has_dpa === false) return 'missing';
  return 'unknown';
};

const mapStage = (lifecycle_stage) => {
  if (lifecycle_stage === 'active') return 'Active';
  return 'Prospect';
};

const ddDoneFromStatus = (status) => {
  if (!status) return 0;
  const m = { draft: 1, in_progress: 2, under_review: 4, completed: 5 };
  return m[status] || 0;
};

// Get DD status map from assessments
const buildDDStatusMap = (assessments) => {
  const map = {};
  assessments.forEach(assessment => {
    const vendorId = assessment.vendor_id;
    if (!map[vendorId]) {
      map[vendorId] = { count: 0, ddDone: 0, status: null };
    }
    map[vendorId].count += 1;
    const ddDone = ddDoneFromStatus(assessment.status);
    if (ddDone > map[vendorId].ddDone) {
      map[vendorId].ddDone = ddDone;
      map[vendorId].status = assessment.status;
    }
  });
  return map;
};

export default function VendorRegistryPage() {
  const navigate = useNavigate();
  const { isDPO, isComplianceReadOnly: isReadOnly, user } = useAuth();
  const { selectedClientId, selectedEntityId } = useGlobalContext();

  // State
  const [vendors, setVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]); // For summary bar - unfiltered
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState(null); // For client staff RBAC banner (client-scoped)

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRisk, setSelectedRisk] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [activeChip, setActiveChip] = useState('all');

  // DD status
  const [ddStatusMap, setDdStatusMap] = useState({});

  // Sorting
  const [sortCol, setSortCol] = useState('risk'); // 'risk', 'vendor', 'entity', 'dpa', 'expiry'
  const [sortDir, setSortDir] = useState('desc'); // 'asc', 'desc'

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch client name for client staff RBAC banner
      if (!isDPO && user?.client_id) {
        try {
          const clientRes = await clientsAPI.getOne(user.client_id);
          setClientName(clientRes.data?.client_name || null);
        } catch (error) {
          console.error('Failed to fetch client name:', error);
          setClientName(null);
        }
      }

      // Build filters from active chip
      let filters = {};
      if (activeChip === 'high-risk') {
        filters.riskTier = 'high';
      } else if (activeChip === 'dpa-missing') {
        filters.dpaMissing = true;
      } else if (activeChip === 'dd-pending') {
        // Will filter client-side after DD fetch
      } else if (activeChip === 'expiring-soon') {
        // Will filter client-side
      }
      if (selectedRisk) filters.riskTier = selectedRisk;
      if (selectedStage) filters.lifecycleStage = selectedStage;

      // Fetch ALL vendors (unfiltered) for summary bar
      // For DPO: pass entity/client filter; for client staff: pass undefined (RBAC handles scoping by client_id)
      const allVendorsRes = await vendorRegistryAPI.getAll(isDPO ? selectedEntityId || undefined : undefined, isDPO ? selectedClientId || undefined : undefined);
      setAllVendors(allVendorsRes.data);

      // Fetch filtered vendors
      const vendorsRes = await vendorRegistryAPI.getAll(isDPO ? selectedEntityId || undefined : undefined, isDPO ? selectedClientId || undefined : undefined, filters);
      let filteredVendors = vendorsRes.data;

      // Client-side filter by search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredVendors = filteredVendors.filter(v =>
          v.vendor_name.toLowerCase().includes(term) ||
          v.vendor_reference?.toLowerCase().includes(term) ||
          v.entity_name?.toLowerCase().includes(term)
        );
      }

      // Fetch all DD assessments (RBAC scopes by entity for DPO, by client for client staff)
      const ddRes = await dueDiligenceAPI.getAll(isDPO ? selectedEntityId || undefined : undefined, isDPO ? selectedClientId || undefined : undefined);
      const ddMap = buildDDStatusMap(ddRes.data || []);
      setDdStatusMap(ddMap);

      // Client-side chip filtering
      if (activeChip === 'dd-pending') {
        filteredVendors = filteredVendors.filter(v => {
          const dd = ddMap[v.id] || { ddDone: 0 };
          return dd.ddDone < 5;
        });
      } else if (activeChip === 'expiring-soon') {
        filteredVendors = filteredVendors.filter(v => {
          const days = v.days_until_expiry;
          return days !== null && days < 60;
        });
      }

      setVendors(filteredVendors);
    } catch (error) {
      console.error('Failed to fetch vendor data:', error);
      toast.error('Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, selectedClientId, selectedRisk, selectedStage, activeChip, searchTerm, isDPO, user?.client_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle sort
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'risk' ? 'desc' : 'asc');
    }
  };

  // Sort vendors
  const sortedVendors = useMemo(() => {
    const sorted = [...vendors];
    sorted.sort((a, b) => {
      let aVal, bVal;

      if (sortCol === 'vendor') {
        aVal = a.vendor_name;
        bVal = b.vendor_name;
      } else if (sortCol === 'entity') {
        aVal = a.entity_name || '';
        bVal = b.entity_name || '';
      } else if (sortCol === 'risk') {
        const riskOrder = { HIGH: 0, MEDIUM: 1, LOW: 2, NOT_ASSESSED: 3 };
        aVal = riskOrder[mapRiskTier(a.risk_assessment?.risk_tier)] || 999;
        bVal = riskOrder[mapRiskTier(b.risk_assessment?.risk_tier)] || 999;
      } else if (sortCol === 'dpa') {
        const dpaOrder = { signed: 0, missing: 1, unknown: 2 };
        aVal = dpaOrder[mapDpa(a.contract_info?.has_dpa ?? a.contract_info?.dpa_signed)] || 999;
        bVal = dpaOrder[mapDpa(b.contract_info?.has_dpa ?? b.contract_info?.dpa_signed)] || 999;
      } else if (sortCol === 'expiry') {
        const aDate = a.contract_info?.contract_end_date || a.contract_info?.expiry_date || '9999-12-31';
        const bDate = b.contract_info?.contract_end_date || b.contract_info?.expiry_date || '9999-12-31';
        aVal = aDate;
        bVal = bDate;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [vendors, sortCol, sortDir]);

  // Summary bar counts - from ALL vendors (unfiltered)
  const stats = useMemo(() => {
    return {
      total: allVendors.length,
      highRisk: allVendors.filter(v => mapRiskTier(v.risk_assessment?.risk_tier) === 'HIGH').length,
      dpaMissing: allVendors.filter(v =>
        mapDpa(v.contract_info?.has_dpa ?? v.contract_info?.dpa_signed) !== 'signed'
      ).length,
      expiringDays: allVendors.filter(v => {
        const days = v.days_until_expiry;
        return days !== null && days !== undefined && days < 60 && days >= 0;
      }).length,
    };
  }, [allVendors]);

  // Render risk pill
  const renderRiskPill = (tier) => {
    const mapped = mapRiskTier(tier);
    const pill = RISK_PILL[mapped];
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: pill.bg,
          color: pill.text,
          fontSize: '11px',
          fontWeight: '500',
        }}
      >
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: pill.dot }} />
        {pill.label}
      </div>
    );
  };

  // Render DPA status with spec colors
  const renderDpa = (dpa_signed) => {
    const status = mapDpa(dpa_signed);
    if (status === 'signed') {
      // Green: #639922 (success dot color from spec)
      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: '#EAF3DE',
          color: '#3B6D11',
          fontSize: '11px',
          fontWeight: '500',
        }}>
          Signed
        </div>
      );
    }
    if (status === 'missing') {
      // Red: #E24B4A (danger dot color from spec)
      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: '#FCEBEB',
          color: '#A32D2D',
          fontSize: '11px',
          fontWeight: '500',
        }}>
          Missing
        </div>
      );
    }
    // Unknown: gray
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: '#F0F0F0',
        color: '#888888',
        fontSize: '11px',
        fontWeight: '500',
      }}>
        Unknown
      </div>
    );
  };

  // Render expiry
  const renderExpiry = (expiry_date, days_until) => {
    if (!expiry_date) return <span className="text-gray-500">—</span>;
    const hasDays = days_until !== null && days_until !== undefined;
    const isExpiring = hasDays && days_until < 60;
    const isExpired = hasDays && days_until < 0;
    const label = new Date(expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return (
      <div className="flex items-center gap-1">
        {isExpiring && <AlertTriangle className={`h-4 w-4 ${isExpired ? 'text-red-500' : 'text-amber-500'}`} />}
        <span className={isExpired ? 'text-red-600 font-medium' : (isExpiring ? 'text-amber-600 font-medium' : 'text-gray-700')}>
          {label}
        </span>
      </div>
    );
  };

  // Render DD status dots
  const renderDd = (vendorId) => {
    const dd = ddStatusMap[vendorId];
    if (!dd) {
      return <span className="text-gray-500">0/5</span>;
    }
    const dots = [];
    for (let i = 0; i < 5; i++) {
      if (i < dd.ddDone) {
        dots.push(<div key={i} className="w-2 h-2 rounded-full bg-green-500" />);
      } else if (i === dd.ddDone && dd.status && dd.status !== 'draft') {
        dots.push(<div key={i} className="w-2 h-2 rounded-full bg-amber-500" />);
      } else {
        dots.push(<div key={i} className="w-2 h-2 rounded-full bg-gray-300" />);
      }
    }
    return (
      <div className="flex items-center gap-1">
        <div className="flex gap-1">{dots}</div>
        <span className="text-xs text-gray-600">{dd.ddDone}/5</span>
      </div>
    );
  };

  // Render stage with spec colors
  const renderStage = (lifecycle_stage) => {
    const stage = mapStage(lifecycle_stage);
    if (stage === 'Active') {
      // Green active badge
      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: '#EAF3DE',
          color: '#3B6D11',
          fontSize: '11px',
          fontWeight: '500',
          border: '1px solid #639922',
        }}>
          Active
        </div>
      );
    }
    // Prospect: outline badge
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: '#888888',
        fontSize: '11px',
        fontWeight: '500',
        border: '1px solid #CCCCCC',
      }}>
        Prospect
      </div>
    );
  };

  // Get action button. Read-only users (viewers + super_admin) always see "View"
  // — the write actions "Start DD" and "Resume" are hidden for them.
  const getActionButton = (vendor) => {
    if (isReadOnly) {
      return {
        label: 'View',
        variant: 'outline',
        action: () => navigate(`/compliance/due-diligence?vendor=${vendor.id}`),
      };
    }
    const dd = ddStatusMap[vendor.id];
    const ddDone = dd?.ddDone || 0;
    const lifecycle = vendor.lifecycle_stage;

    if (lifecycle === 'prospect') {
      return {
        label: '+ Start DD',
        variant: 'default',
        action: () => navigate(`/compliance/due-diligence/new?vendor=${vendor.id}`)
      };
    }
    if (lifecycle === 'active' && ddDone < 5) {
      return {
        label: 'Resume',
        variant: 'outline',
        action: () => navigate(`/compliance/due-diligence?vendor=${vendor.id}`)
      };
    }
    return {
      label: 'View',
      variant: 'outline',
      action: () => navigate(`/compliance/due-diligence?vendor=${vendor.id}`)
    };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Vendor registry</h1>
            {/* Role Tabs - Pill Style */}
            <div className="flex gap-2 mt-4 bg-gray-200 dark:bg-gray-800 p-1 rounded-lg w-fit">
              <button
                disabled
                style={{
                  padding: '8px 16px',
                  backgroundColor: isDPO ? 'white' : 'transparent',
                  color: isDPO ? '#1A1A2E' : '#888888',
                  fontSize: '13px',
                  fontWeight: isDPO ? '500' : '400',
                  cursor: 'default',
                  border: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                }}
                className="dark:bg-gray-700 dark:text-gray-300"
              >
                DPO / compliance lead
              </button>
              <button
                disabled
                style={{
                  padding: '8px 16px',
                  backgroundColor: !isDPO ? 'white' : 'transparent',
                  color: !isDPO ? '#1A1A2E' : '#888888',
                  fontSize: '13px',
                  fontWeight: !isDPO ? '500' : '400',
                  cursor: 'default',
                  border: 'none',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                }}
                className="dark:bg-gray-700 dark:text-gray-300"
              >
                Client staff
              </button>
            </div>
          </div>
          {!isReadOnly && (
            <Button
              onClick={() => navigate('/compliance/vendors/simplified/new')}
              className="gap-2 ml-4"
            >
              <Plus className="h-4 w-4" />
              Add Vendor
            </Button>
          )}
        </div>

        {/* RBAC Banner - Client staff only (client-scoped) */}
        {!isDPO && clientName && (
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <span className="text-sm text-blue-900 dark:text-blue-200">
              • <span className="font-medium">Scoped to {clientName}</span> — you can only view vendors assigned to your client
            </span>
          </div>
        )}

        {/* Summary Bar */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Total vendors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.total}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isDPO ? 'across all entities' : 'your entity'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                High risk
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.highRisk}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">require review</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                DPA gaps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.dpaMissing}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">missing DPA</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Expiring &lt;60d
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.expiringDays}</div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">expiring soon</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Row */}
        <div className="bg-gray-50 dark:bg-gray-950 p-4 rounded-lg space-y-3">
          <div className="flex gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search vendors, entities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Risk Filter */}
            <Select value={selectedRisk || '__all__'} onValueChange={(val) => setSelectedRisk(val === '__all__' ? '' : val)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All risk tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All risk tiers</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="not_assessed">Not assessed</SelectItem>
              </SelectContent>
            </Select>

            {/* Stage Filter */}
            <Select value={selectedStage || '__all__'} onValueChange={(val) => setSelectedStage(val === '__all__' ? '' : val)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All stages</SelectItem>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="under_review">Under review</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Quick-Filter Chips */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'dpa-missing', 'high-risk', 'dd-pending', 'expiring-soon'].map(chip => {
            const labels = {
              'all': 'All vendors',
              'dpa-missing': 'DPA missing',
              'high-risk': 'High risk',
              'dd-pending': 'DD pending',
              'expiring-soon': 'Expiring soon',
            };
            const isActive = activeChip === chip;
            return (
              <button
                key={chip}
                onClick={() => setActiveChip(chip)}
                className={cn(
                  'px-3 py-1 rounded-full text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                )}
              >
                {labels[chip]}
              </button>
            );
          })}
        </div>

        {/* Row count */}
        {!loading && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {sortedVendors.length} vendor{sortedVendors.length !== 1 ? 's' : ''} shown
          </p>
        )}

        {/* Vendor Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : sortedVendors.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                <AlertTriangle className="h-8 w-8 mb-2 opacity-50" />
                <p>No vendors found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-950">
                    <TableHead
                      onClick={() => handleSort('vendor')}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 w-32"
                    >
                      <div className="flex items-center gap-1">
                        VENDOR
                        {sortCol === 'vendor' && (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
                      </div>
                    </TableHead>
                    {isDPO && (
                      <TableHead
                        onClick={() => handleSort('entity')}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 w-32"
                      >
                        <div className="flex items-center gap-1">
                          ENTITY
                          {sortCol === 'entity' && (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
                        </div>
                      </TableHead>
                    )}
                    <TableHead
                      onClick={() => handleSort('risk')}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 w-32"
                    >
                      <div className="flex items-center gap-1">
                        RISK
                        {sortCol === 'risk' && (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => handleSort('dpa')}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 w-24"
                    >
                      <div className="flex items-center gap-1">
                        DPA
                        {sortCol === 'dpa' && (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => handleSort('expiry')}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-900 w-32"
                    >
                      <div className="flex items-center gap-1">
                        EXPIRY
                        {sortCol === 'expiry' && (sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
                      </div>
                    </TableHead>
                    <TableHead className="w-28">DUE DILIGENCE</TableHead>
                    <TableHead className="w-24">STAGE</TableHead>
                    <TableHead className="text-right w-28">ACTIONS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedVendors.map(vendor => {
                    const action = getActionButton(vendor);
                    return (
                      <TableRow
                        key={vendor.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-950"
                        onClick={() => navigate(`/compliance/vendors/simplified/${vendor.id}`)}
                      >
                        <TableCell className="py-3">
                          <div className="font-mono text-xs text-gray-500">{vendor.vendor_reference || '—'}</div>
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{vendor.vendor_name}</div>
                        </TableCell>
                        {isDPO && (
                          <TableCell className="truncate max-w-xs text-sm text-slate-900 dark:text-slate-100">
                            {vendor.entity_name}
                          </TableCell>
                        )}
                        <TableCell>
                          {renderRiskPill(vendor.risk_assessment?.risk_tier)}
                        </TableCell>
                        <TableCell>
                          {renderDpa(vendor.contract_info?.has_dpa ?? vendor.contract_info?.dpa_signed)}
                        </TableCell>
                        <TableCell>
                          {renderExpiry(
                            vendor.contract_info?.contract_end_date ?? vendor.contract_info?.expiry_date,
                            vendor.days_until_expiry
                          )}
                        </TableCell>
                        <TableCell>
                          {renderDd(vendor.id)}
                        </TableCell>
                        <TableCell>
                          {renderStage(vendor.lifecycle_stage)}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant={action.variant}
                            size="sm"
                            onClick={action.action}
                          >
                            {action.label}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
